import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { Canevas, Question } from '../../onboarding/canevas-types'

/**
 * VUE PAPIER de la grille d'entretien : une feuille blanche (titres, consignes,
 * questions avec cases et lignes à remplir à la main, cases de notes 1-5,
 * synthèse, signature), pilotée par le canevas — donc par le fichier de contenu.
 *
 * `window.print()` + la feuille de style `@media print` : tant que la vue est
 * ouverte, `body.mode-papier` masque tout le reste (la console, les fiches,
 * les modales) ; seule la grille sort sur papier. Elle est montée dans un
 * portail sous `<body>`, comme les autres superpositions.
 */
const NOTES = [1, 2, 3, 4, 5]

function ReponsePapier({ q }: { q: Question }) {
  if (q.type === 'note') {
    return (
      <div className="cases">
        {NOTES.map((n) => <span className="case" key={n}>{n}</span>)}
        <span style={{ fontSize: 11, color: '#444' }}>1 = insuffisant · 5 = excellent</span>
      </div>
    )
  }
  if (q.type === 'oui_non') {
    return (
      <div className="cases">
        <span className="case lib">Oui</span>
        <span className="case lib">Non</span>
      </div>
    )
  }
  return (
    <div style={{ marginTop: 6 }}>
      <div className="ligne" />
      <div className="ligne" />
      <div className="ligne" />
    </div>
  )
}

export function GrillePapier({
  canevas,
  candidat,
  surFermer,
}: {
  canevas: Canevas
  /** Nom pré-imprimé ; vide : ligne à remplir. */
  candidat?: string
  surFermer: () => void
}) {
  useEffect(() => {
    document.body.classList.add('mode-papier')
    const h = (e: KeyboardEvent) => e.key === 'Escape' && surFermer()
    document.addEventListener('keydown', h)
    return () => {
      document.body.classList.remove('mode-papier')
      document.removeEventListener('keydown', h)
    }
  }, [surFermer])

  let numero = 0
  return createPortal(
    <div className="vue-papier">
      <div className="vue-papier-barre no-print">
        <span className="dos-note" style={{ marginRight: 'auto' }}>Aperçu de la feuille — format A4.</span>
        <button className="btn btn-accent btn-sm" onClick={() => window.print()}>Imprimer</button>
        <button className="btn btn-outline btn-sm" onClick={surFermer}>Fermer</button>
      </div>
      <div className="feuille">
        <h1>Grille d'entretien — candidat livreur SwiftEats</h1>
        <div style={{ fontSize: 11.5, color: '#444' }}>Canevas version {String(canevas.version)}</div>

        <div className="meta">
          <div className="champ-papier"><span>Candidat :</span>{candidat ? <b>{candidat}</b> : <i />}</div>
          <div className="champ-papier"><span>Date :</span><i /></div>
          <div className="champ-papier"><span>Intervieweur :</span><i /></div>
          <div className="champ-papier"><span>Durée :</span><i /></div>
        </div>

        {canevas.sections.map((s, i) => (
          <div key={s.id}>
            <h2>{i + 1}. {s.titre}</h2>
            {s.consigne && <p className="consigne">{s.consigne}</p>}
            {s.questions.map((q) => {
              numero += 1
              return (
                <div className="q" key={q.id}>
                  <div className="tq">
                    {numero}. {q.texte}
                    {q.eliminatoire && <span className="elim">Éliminatoire</span>}
                  </div>
                  {q.aide && <div className="aide">{q.aide}</div>}
                  <ReponsePapier q={q} />
                </div>
              )
            })}
          </div>
        ))}

        <div style={{ breakInside: 'avoid' }}>
          <h2>Synthèse</h2>
          {canevas.synthese.criteres.map((k) => (
            <div className="q" key={k.id}>
              <div className="tq">{k.libelle}</div>
              <div className="cases">{NOTES.map((n) => <span className="case" key={n}>{n}</span>)}</div>
            </div>
          ))}
          <div className="q">
            <div className="tq">Score moyen des notes (à calculer)</div>
            <div className="cases"><span className="case" style={{ minWidth: 70 }}>&nbsp;</span><span style={{ fontSize: 11 }}>/ 5</span></div>
          </div>
          <div className="q">
            <div className="tq">Recommandation</div>
            <div className="cases">
              <span className="case lib">Retenir</span>
              <span className="case lib">À revoir</span>
              <span className="case lib">Refuser</span>
            </div>
            <div className="aide">Une question éliminatoire déclenchée interdit « Retenir ».</div>
          </div>
          <div className="q">
            <div className="tq">Notes</div>
            <div className="ligne" /><div className="ligne" /><div className="ligne" />
          </div>
          <div className="signature">
            <div className="zone">Signature de l'intervieweur</div>
            <div className="zone">Date</div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
